import React, { createContext, useEffect, useState, ReactNode } from 'react'
import { DocumentWithText } from '../lib/api/types/document.interfaces'

interface SnackBarData {
	severity: string | null
	undoFunction: (() => void) | null
	message: string | null
	open: boolean
}

interface AppContextType {
	screenSize: number
	setScreenSize: (val: number) => void
	isSidebarOpen: boolean
	setIsSidebarOpen: (val: boolean) => void
	containerWidth: number
	setContainerWidth: (val: number) => void
	snackBarData: {
		severity: string | null
		undoFunction: (() => void) | null
		message: string | null
		open: boolean
	}
	setSnackBarData: (val: {
		severity: string | null
		undoFunction: any
		message: string | null
		open: boolean
	}) => void
	searchResults: { startId?: string | null; data: DocumentWithText[] }
	setSearchResults: React.Dispatch<
		React.SetStateAction<{ startId?: string | null; data: DocumentWithText[] }>
	>
	darkMode: boolean
	setDarkMode: (val: boolean) => void
	isApp: boolean
}

const AppContext = createContext<AppContextType>({
	screenSize: 0,
	setScreenSize: (val) => { },
	isSidebarOpen: false,
	setIsSidebarOpen: (val) => { },
	containerWidth: 0,
	setContainerWidth: (val) => { },
	snackBarData: {
		severity: null,
		undoFunction: null,
		message: null,
		open: false,
	},
	setSnackBarData: (val) => { },
	searchResults: { startId: null, data: [] },
	setSearchResults: (val) => { },
	darkMode: false,
	setDarkMode: (val) => { },
	isApp: false,
})

export const AppProvider = ({ children }: { children: ReactNode }) => {
	const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false)
	const [screenSize, setScreenSize] = useState<number>(window.innerWidth)
	const [containerWidth, setContainerWidth] = useState<number>(0)
	const [snackBarData, setSnackBarData] = useState<SnackBarData>({
		severity: null,
		undoFunction: null,
		message: null,
		open: false,
	})
	const [searchResults, setSearchResults] = useState<{
		startId?: string | null
		data: any[]
	}>({
		data: [],
	})
	const [darkMode, setDarkMode] = useState<boolean>(false)

	const isApp =
		document.cookie.includes('isApp=true') ||
		localStorage.getItem('isApp') === 'true'

	useEffect(() => {
		const handleResize = (): void => setScreenSize(window.innerWidth)
		window.addEventListener('resize', handleResize)
		return (): void => window.removeEventListener('resize', handleResize)
	}, [])

	return (
		<AppContext.Provider
			value={{
				screenSize,
				setScreenSize,
				isSidebarOpen,
				setIsSidebarOpen,
				containerWidth,
				setContainerWidth,
				snackBarData,
				setSnackBarData,
				searchResults,
				setSearchResults,
				darkMode,
				setDarkMode,
				isApp,
			}}
		>
			{children}
		</AppContext.Provider>
	)
}

export default AppContext
